import type { SimulationListEntryDto } from '@/api';
import { connectColors } from '@/theme/colors';
import {
  Button as ConnectButton,
  Modal,
  Tag,
} from '@lego/connect-components-react';
import {
  Flex,
  HorizontalStack,
  IconButton,
  Text,
  VerticalStack,
} from '@lego/klik-ui';
import {
  ChevronLeftSolid,
  ChevronRightSolid,
  StatusCircleExclamation,
} from '@lego/klik-ui-icons';
import { AnimatePresence, motion } from 'framer-motion';
import React, { type RefObject, useState } from 'react';

type Props = {
  data: SimulationListEntryDto[];
  isOpen: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onConfirmDelete: (simulations: SimulationListEntryDto[]) => void;
  isDeleting: boolean;
};

export const DeleteSimulationModal = ({
  isOpen,
  data,
  triggerRef,
  onClose,
  onConfirmDelete,
  isDeleting,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0); // 1 for next, -1 for previous

  const handleNext = () => {
    if (currentIndex < data.length - 1) {
      setDirection(1);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const items = data.map((simulation) => (
    <React.Fragment key={simulation.simulationId}>
      <VerticalStack
        backgroundColor={connectColors.core.gray[10]}
        width="100%"
        border={`1px solid ${connectColors.core.red[900]}`}
        padding="16px"
        gap="4px"
        alignItems="start"
      >
        <VerticalStack gap={0} alignItems="start">
          <Text color={connectColors.core.gray[900]} className="body-sm-medium">
            Simulation
          </Text>
          <Text className="body-lg-regular">{simulation.simulationName}</Text>
        </VerticalStack>
        <VerticalStack gap={0} alignItems="start" width="100%">
          <Text color={connectColors.core.gray[900]} className="body-sm-medium">
            Price list(s)
          </Text>
          <HorizontalStack gap="8px" wrap="wrap">
            {simulation.priceUnitIds?.map((puId) => (
              <Tag
                variant="brand"
                key={puId}
                size="small"
                aria-label={`Price unit ${puId}`}
              >
                {puId.toString()}
              </Tag>
            ))}
          </HorizontalStack>
        </VerticalStack>
        <VerticalStack gap={0} alignItems="start">
          <Text color={connectColors.core.gray[900]} className="body-sm-medium">
            Planning year
          </Text>
          <Text className="body-md-medium">{simulation.year}</Text>
        </VerticalStack>
        <VerticalStack gap={0} alignItems="start">
          <Text color={connectColors.core.gray[900]} className="body-sm-medium">
            Created by
          </Text>
          <Text className="body-lg-regular">{simulation.createdBy.name}</Text>
        </VerticalStack>
      </VerticalStack>
    </React.Fragment>
  ));

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -100 : 100,
      opacity: 0,
    }),
  };

  const isMoreThanOne = data.length > 1;

  const deleteText =
    data.length === 1
      ? 'Delete 1 Simulation'
      : `Delete ${data.length} Simulations`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      triggerRef={triggerRef}
    >
      <Modal.Title>{deleteText}</Modal.Title>
      <Modal.Content>
        {data.length > 0 && (
          <HorizontalStack
            width="100%"
            gap="16px"
            alignItems="center"
            justifyContent="space-between"
          >
            {isMoreThanOne && (
              <IconButton
                aria-label="Previous simulation"
                icon={<ChevronLeftSolid />}
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                variant="ghost"
                color="black"
                size="32px"
                padding="4px"
              />
            )}
            <VerticalStack
              gap="12px"
              alignItems="center"
              width="100%"
              flex="1"
              style={{ position: 'relative', overflow: 'hidden' }}
            >
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    opacity: { duration: 0.2 },
                  }}
                  style={{ width: '100%' }}
                >
                  {items[currentIndex]}
                </motion.div>
              </AnimatePresence>
              {isMoreThanOne && (
                <Text
                  className="body-sm-medium"
                  color={connectColors.core.gray[900]}
                >
                  {currentIndex + 1} of {data.length}
                </Text>
              )}
            </VerticalStack>
            {isMoreThanOne && (
              <IconButton
                aria-label="Next simulation"
                icon={<ChevronRightSolid />}
                onClick={handleNext}
                disabled={currentIndex === data.length - 1}
                variant="ghost"
                color="black"
                size="32px"
                padding="4px"
              />
            )}
          </HorizontalStack>
        )}
        <Flex gap="8px">
          <StatusCircleExclamation
            color={connectColors.core.red[900]}
            width="24px"
            height="24px"
          />
          <VerticalStack alignItems="start" gap={0}>
            <Text>
              The simulations and all their snapshots will be deleted.
            </Text>
            <Text>This action can not be undone.</Text>
          </VerticalStack>
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <ConnectButton
          aria-label="Cancel"
          disabled={isDeleting}
          appearance="ghost"
          variant="neutral"
          onClick={onClose}
        >
          Cancel
        </ConnectButton>
        <ConnectButton
          aria-label="Delete"
          disabled={isDeleting}
          variant="negative"
          onClick={() => onConfirmDelete(data)}
        >
          {deleteText}
        </ConnectButton>
      </Modal.Footer>
    </Modal>
  );
};
